import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '../stores/auth-store';

import { useLogout } from './useAuth';

const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'visibilitychange'] as const;

/**
 * Déconnecte automatiquement l'utilisateur staff (IT/ADMIN) après
 * `timeoutMs` d'inactivité. Pour les clients on est plus tolérant : pas
 * d'auto-logout (mais le refresh token expirera de toute façon).
 */
export function useInactivityLogout(timeoutMs = DEFAULT_TIMEOUT_MS): void {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (status !== 'authenticated' || !user) return;
    const isStaff = user.role === 'IT' || user.role === 'ADMIN';
    if (!isStaff) return;

    function scheduleLogout() {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        logout.mutate(undefined, {
          onSettled: () => navigate('/login', { replace: true, state: { from: 'inactivity' } }),
        });
      }, timeoutMs);
    }

    function onActivity() {
      if (document.visibilityState === 'hidden') return;
      scheduleLogout();
    }

    scheduleLogout();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity));

    return () => {
      if (timer.current) clearTimeout(timer.current);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [status, user, timeoutMs, logout, navigate]);
}
